import { useForm } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2Icon } from "lucide-react";
import { Input } from "./Input";
import { authClient, SessionPayload } from "@/lib/auth/token-manager";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PasswordInput } from "../../ui/password-input";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";
import { newPasswordSchema } from "@/lib/validation/password";
import { useState } from "react";
import { isAxiosError } from "axios";

const loginSchema = z.object({
  email: z.email("Invalid email address"),
  password: z.string().min(1, "Enter your password"),
});

const registerSchema = z.object({
  email: z.email("Invalid email address"),
  password: newPasswordSchema,
});

export type AuthenticateForm = {
  email: string;
  password: string;
};

type Mode = "login" | "register";

const messageOf = (err: unknown, fallback: string) => {
  if (isAxiosError(err)) {
    const message = err.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(" ");
    }
    if (typeof message === "string") {
      return message;
    }
  }
  return fallback;
};

export const AuthenticationAuthenticate = ({
  onLogin,
  goToForget,
}: {
  onLogin: (data: SessionPayload) => void;
  goToForget: () => void;
}) => {
  const [mode, setMode] = useState<Mode>("login");

  const { isPending, isError, error, mutate, reset } = useMutation({
    mutationKey: ["login/register", mode],
    mutationFn: async ({ email, password }: AuthenticateForm) => {
      const res = await authClient
        .post(mode === "login" ? "/auth/login" : "/auth/register", {
          email,
          password,
        })
        .catch(err => {
          throw new Error(
            messageOf(err, "Something went wrong, please try again."),
          );
        });
      return res.data.data as SessionPayload;
    },
    onSuccess: res => {
      captureEvent("onboarding_auth_succeeded", {
        method: "email_password",
        newUser: !!res.new_user,
      });
      onLogin(res);
    },
    onError: err => {
      captureEvent("onboarding_auth_failed", {
        method: "email_password",
        reason: sanitizeErrorReason(err),
      });
    },
  });

  const form = useForm<AuthenticateForm>({
    resolver: zodResolver(mode === "login" ? loginSchema : registerSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const switchMode = () => {
    setMode(current => (current === "login" ? "register" : "login"));
    form.clearErrors();
    reset();
  };

  const handleSubmit = (values: { email: string; password: string }) => {
    captureEvent("onboarding_auth_submitted", {
      method: "email_password",
    });
    mutate({ ...values });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-grow flex-col justify-between"
      >
        <div className="flex flex-grow flex-col gap-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email..."
                  {...field}
                />
                {error && <FormMessage>{error.message}</FormMessage>}
              </FormItem>
            )}
          />
          <FormField
            name="password"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <PasswordInput
                  value={field.value}
                  onValueChange={field.onChange}
                  showStrength={mode === "register"}
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                />
                {error && <FormMessage>{error.message}</FormMessage>}
                {mode === "register" && !error ? (
                  <p className="text-muted-foreground text-xs">
                    At least 12 characters, with a number and a symbol.
                  </p>
                ) : null}
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-grow flex-col justify-end gap-4">
          {isError ? (
            <Alert variant="destructive" className="-mb-8 border-none p-0">
              <AlertDescription>
                {error?.message || "An error occurred, please try again."}
              </AlertDescription>
            </Alert>
          ) : null}
          <Button
            type="submit"
            variant="default"
            disabled={isPending}
            className="mt-6 h-auto rounded-xl py-3"
          >
            {isPending ? (
              <>
                <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
                {mode === "login" ? "Signing in..." : "Creating account..."}
              </>
            ) : mode === "login" ? (
              "Log in"
            ) : (
              "Create account"
            )}
          </Button>
          <div className="-mt-2 flex items-center justify-between">
            <Button
              type="button"
              variant="link"
              className="px-0"
              onClick={switchMode}
            >
              {mode === "login"
                ? "New to UniDeck? Create an account"
                : "Already have an account? Log in"}
            </Button>
            {mode === "login" ? (
              <Button
                type="button"
                variant="link"
                className="px-0"
                onClick={goToForget}
              >
                Forgot password?
              </Button>
            ) : null}
          </div>
        </div>
      </form>
    </Form>
  );
};
